import {
  ChatRendererProvider,
  createMessage,
  cn,
} from '@form-factory/chatbot-ui';
import {
  ToolNames,
  type UsageLimitsErrorToolArgs,
} from '@form-factory/schema/contracts';
import { ChatRole, ThreadMode, ToolType } from '@form-factory/schema/enums';
import { ErrorCodes } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { v7 as uuidV7 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

import { useChatConfig } from '@/contexts/ChatConfigProvider';
import { useNavigate } from '@/hooks/useNavigate';
import { useProvider } from '@/hooks/useProvider';
import { useUser } from '@/hooks/useUser';
import { hasPaymentParam, removePaymentParam } from '@/lib/utils';
import type { UseChatParams } from '@/routes/chat/chatTypes';
import { useChatThreads } from '@/routes/chat/useChatThreads';
import { threadKeys } from '@/services/queryClient';
import { useBoundStore } from '@/store/store';

import { ChatbotChatRenderer } from './ChatbotChatRenderer';
import { useActivityPing } from './useActivityPing';
import { useChat } from './useChat';
import { InputBox } from '../../components/inputBox/InputBox';

export function Chat() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { logo } = useProvider();
  const { isEfitnessAuthenticated } = useUser();
  const { chatId, chatbotConfig } = useChatConfig();

  const [faqIndex, setFaqIndex] = useState<number | null>(null);
  const [messageCounter, setMessageCounter] = useState(0);
  const {
    threadId,
    setThreadId,
    opened,
    setUnread,
    childrenCount,
    isInFAQLoop,
    currentFAQId,
    setResetConversation,
  } = useBoundStore(
    useShallow(state => ({
      threadId: state.threadId,
      setThreadId: state.setThreadId,
      opened: state.opened,
      setUnread: state.setUnread,
      childrenCount: state.childrenCount,
      isInFAQLoop: state.isInFAQLoop,
      currentFAQId: state.currentFAQId,
      setResetConversation: state.setResetConversation,
    })),
  );

  const { handleThreadRefresh, threads, activeThread } = useChatThreads({
    chatId,
    threadId,
    setThreadId,
    opened,
  });

  const queryClient = useQueryClient();

  /**
   * Activity ping to keep track of user activity, current
   * conversation mode and handle admin takeover.
   */
  useActivityPing(threadId);

  /**
   * Always set unread, this should be cleaned up when clicking close button.
   * Set unread state and refresh threads when receiving a message
   */
  const handleMessage = useCallback(() => {
    setUnread(1);

    if (threads) {
      handleThreadRefresh();
    }
  }, [setUnread, threads, handleThreadRefresh]);

  /**
   * Error handler for the chat.
   */
  const handleError = useCallback<Required<UseChatParams>['onError']>(
    async (error, insert) => {
      /**
       * Handle API errors
       */
      console.error(error);
      if (typeof error === 'object' && error !== null && 'errorCode' in error) {
        const { errorCode, data = {} } = error as {
          errorCode: string;
          data?: Record<string, unknown>;
        };

        // Use special tool for the usage limits
        if (errorCode === ErrorCodes.USAGE_LIMITS_EXCEEDED) {
          return insert({
            id: uuidV7(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            role: ChatRole.Assistant,
            toolCalls: [
              {
                name: ToolNames.UsageLimitsError,
                args: data
                  ? (data as UsageLimitsErrorToolArgs)
                  : ({ operator: null } satisfies UsageLimitsErrorToolArgs),
                type: ToolType.Tool,
                index: 0,
                id: 'usage-limits-error',
              },
            ],
          });
        }

        if (errorCode === ErrorCodes.AGENT_DOES_NOT_EXIST) {
          return insert(
            t`We are sorry, but the agent does not exist. Please try again later.`,
            { role: ChatRole.Status },
          );
        }
      }

      /**
       * Handle message errors
       */
      if (typeof error === 'string') {
        return insert(error, { role: ChatRole.Status });
      }

      /**
       * Handle all other errors
       */
      return insert(
        t`An error occurred, the chat is currently unavailable. Please try again later.`,
        { role: ChatRole.Status },
      );
    },
    [t],
  );

  /**
   * This handles the chat logic, it fetches the messages from the API
   * and sends new messages to the API.
   */
  const {
    input,
    handleInputChange,
    messages,
    handleSubmit,
    isLoading,
    isFetching,
    abort,
    insert,
    append,
  } = useChat({
    threadId,
    chatId,
    activateLiveChat: activeThread?.mode === ThreadMode.Human,
    initialMessages: [
      createMessage({
        message:
          chatbotConfig?.initialMessage ||
          t`Hi, I am Tina, your virtual assistant! How can I help you?`,
        role: ChatRole.Assistant,
      }),
    ],
    onError: handleError,
    onMessage: handleMessage,
  });

  // Payment logic
  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (hasPaymentParam()) {
      removePaymentParam();

      insert(
        createMessage({
          message: t`Thank you for creating a membership. You will be notified about payment processing via email.`,
          role: ChatRole.Assistant,
        }),
      );
    }
  }, [isLoading, insert, t]);

  /**
   * Following two useEffects are used to reset the faqIndex when the threadId changes
   * or when the messages change. This is used to always make sure we show the FAQs
   * after the last AI message upon conversation reset or page reload.
   */
  useEffect(() => {
    setFaqIndex(null);
  }, [threadId]);

  const wrapHandleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      // Update the message counter only for AI mode
      if (activeThread?.mode === ThreadMode.AI) {
        setMessageCounter(value => value + 1);
      }

      return handleSubmit(e);
    },
    [activeThread?.mode, handleSubmit],
  );

  useEffect(() => {
    if (messages && messages.length > 0) {
      const lastAiMessageIndex = messages.findLastIndex(
        msg => msg.role === ChatRole.Assistant,
      );

      if (
        faqIndex === null ||
        (childrenCount && childrenCount > 0 && isInFAQLoop) ||
        (currentFAQId && currentFAQId !== '')
      ) {
        setFaqIndex(lastAiMessageIndex === -1 ? 0 : lastAiMessageIndex);
      }
    }
  }, [messages, faqIndex, childrenCount, isInFAQLoop, currentFAQId]);

  // Handle reset tooltip visibility
  useEffect(() => {
    if (messageCounter === 2) {
      setResetConversation(true);
    }
  }, [messageCounter, setResetConversation]);

  /**
   * Threads are empty when the user visits that chat for the first time.
   * First message is welcome message, so we have to fetch the threads
   * after the first user message is sent.
   */
  useEffect(() => {
    if (
      !activeThread &&
      !isFetching &&
      !isLoading &&
      messages.filter(m => m.role === ChatRole.User).length
    ) {
      queryClient.invalidateQueries({
        queryKey: threadKeys.chatLists(chatId),
      });
    }
  }, [messages, isFetching, isLoading, queryClient, chatId, activeThread]);

  return (
    <div className='relative flex h-full flex-col'>
      <div className='flex grow flex-col-reverse overflow-hidden overflow-y-auto bg-chat-background px-5 py-4 text-chat-foreground will-change-transform'>
        <ChatRendererProvider
          insert={insert}
          append={append}
          navigate={navigate.navigate}
          logo={logo}
          isEfitnessAuthenticated={isEfitnessAuthenticated}
        >
          <ChatbotChatRenderer
            insert={insert}
            isInFAQLoop={isInFAQLoop}
            faqIndex={faqIndex}
            messages={messages}
            isLoading={isLoading}
            isFetching={isFetching}
            thread={activeThread}
          />
        </ChatRendererProvider>
      </div>
      <div
        className={cn(
          'relative shrink-0 bg-chat-background-secondary px-4 py-3',
        )}
      >
        <InputBox
          onSubmit={wrapHandleSubmit}
          value={input}
          onChange={handleInputChange}
          onAbort={abort}
          isFetching={isFetching}
          isLoading={isLoading}
          disabledInput={isLoading}
          maxLength={2000}
        />
      </div>
    </div>
  );
}
